import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Paper,
    Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { formatDateTime } from '../../utils/dateUtils';
import {
    WebhookDeliveryView, WebhookEndpointView, WebhookService, WebhookSettings,
} from '../../services/integration.service';

/** Outbound webhooks: endpoints (inactive until switched on), a signed test, recent deliveries with retry, and "send now". */
const WebhooksPanel: React.FC = () => {
    const toast = useToast();
    const [settings, setSettings] = useState<WebhookSettings | null>(null);
    const [endpoints, setEndpoints] = useState<WebhookEndpointView[]>([]);
    const [deliveries, setDeliveries] = useState<WebhookDeliveryView[]>([]);
    const [editing, setEditing] = useState<WebhookEndpointView | 'new' | null>(null);
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [events, setEvents] = useState<string[]>([]);
    const [active, setActive] = useState(false);
    const [secret, setSecret] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, e, d] = await Promise.all([WebhookService.settings(), WebhookService.list(), WebhookService.deliveries()]);
            setSettings(s); setEndpoints(e); setDeliveries(d);
        } catch (err) {
            toast.showApiError(err, 'Failed to load webhooks');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const openEditor = (e: WebhookEndpointView | 'new') => {
        setEditing(e);
        setName(e === 'new' ? '' : e.name);
        setUrl(e === 'new' ? '' : e.url);
        setEvents(e === 'new' ? [] : e.events);
        setActive(e === 'new' ? false : e.active);
    };
    const toggleEvent = (ev: string) => setEvents(cur => (cur.includes(ev) ? cur.filter(x => x !== ev) : [...cur, ev]));

    const save = async () => {
        if (busy || !editing) return;
        setBusy(true);
        try {
            const body = { name: name.trim(), url: url.trim(), events, active };
            if (editing === 'new') {
                const res = await WebhookService.create(body);
                setSecret(res.secret);
            } else {
                await WebhookService.update(editing.id, body);
            }
            setEditing(null);
            await load();
        } catch (err) {
            toast.showApiError(err, 'Could not save the webhook');
        } finally {
            setBusy(false);
        }
    };

    const act = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
        try {
            await fn();
            toast.showSuccess(ok);
            await load();
        } catch (err) {
            toast.showApiError(err, fail);
        }
    };

    const rotate = async (e: WebhookEndpointView) => {
        if (!window.confirm('Replace the signing secret? The receiver must be updated with the new one.')) return;
        try {
            const res = await WebhookService.rotateSecret(e.id);
            setSecret(res.secret);
        } catch (err) {
            toast.showApiError(err, 'Could not rotate the secret');
        }
    };

    const remove = async (e: WebhookEndpointView) => {
        if (!window.confirm(`Delete the webhook "${e.name}"?`)) return;
        await act(() => WebhookService.remove(e.id), 'Webhook deleted', 'Could not delete the webhook');
    };

    return (
        <Box data-help-page="data.webhooks.page">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each event is POSTed as JSON with an <code>X-Kervolt-Signature</code> header (<code>t=timestamp,v1=HMAC-SHA256</code>
                over <code>timestamp.body</code>). Failed deliveries retry after 1 minute, 5 minutes, 30 minutes, 2 hours and 12 hours.
                Only public https addresses are accepted. New webhooks start switched off.
            </Typography>
            {settings && !settings.configured && (
                <Alert severity="warning" sx={{ mb: 2 }}>Webhooks are not configured on this server (the signing key is not set).</Alert>
            )}
            {settings && settings.configured && !settings.schedulerEnabled && (
                <Alert severity="info" sx={{ mb: 2 }}>Automatic delivery is off on this server. Use "Send queued now" to deliver.</Alert>
            )}
            {secret && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="revealed-secret" onClose={() => setSecret(null)}>
                    Copy the signing secret now; it is shown only once: <code>{secret}</code>
                </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Button data-help="data.webhooks.action.add" {...helpWhy(!settings?.configured ? { kind: 'status', message: 'Webhooks are not set up on this server yet (the signing key is missing). Ask your platform administrator.' } : null)} variant="contained" disabled={!settings?.configured} onClick={() => openEditor('new')}>Add webhook</Button>
                <Button data-help="data.webhooks.action.sendNow" {...helpWhy(!settings?.configured ? { kind: 'status', message: 'Webhooks are not set up on this server yet (the signing key is missing). Ask your platform administrator.' } : null)} variant="outlined" disabled={!settings?.configured}
                    onClick={() => act(async () => { const r = await WebhookService.sendNow(); toast.showSuccess(`${r.attempted} deliveries attempted`); }, 'Done', 'Send failed')}>
                    Send queued now
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small" data-help-section="data.webhooks.endpoints">
                    <TableHead>
                        <TableRow><TableCell data-help="data.webhooks.col.name">Name</TableCell><TableCell data-help="data.webhooks.col.url">URL</TableCell><TableCell data-help="data.webhooks.col.events">Events</TableCell><TableCell data-help="data.webhooks.col.active">Active</TableCell><TableCell data-help="data.webhooks.col.pending">Pending</TableCell><TableCell /></TableRow>
                    </TableHead>
                    <TableBody>
                        {endpoints.length === 0 && <TableRow><TableCell colSpan={6}>No webhooks yet.</TableCell></TableRow>}
                        {endpoints.map(e => (
                            <TableRow key={e.id}>
                                <TableCell>{e.name}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-all' }}>{e.url}</TableCell>
                                <TableCell>{e.events.join(', ')}</TableCell>
                                <TableCell><Chip size="small" label={e.active ? 'On' : 'Off'} color={e.active ? 'success' : 'default'} /></TableCell>
                                <TableCell>{e.pendingCount}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Button data-help="data.webhooks.action.edit" size="small" onClick={() => openEditor(e)}>Edit</Button>
                                    <Button data-help="data.webhooks.action.test" size="small" onClick={() => act(() => WebhookService.test(e.id), 'Test queued', 'Could not queue the test')}>Test</Button>
                                    <Button data-help="data.webhooks.action.rotate" size="small" onClick={() => rotate(e)}>New secret</Button>
                                    <Button data-help="data.webhooks.action.delete" size="small" color="error" onClick={() => remove(e)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom>Recent deliveries</Typography>
            <TableContainer component={Paper}>
                <Table size="small" data-help-section="data.webhooks.deliveries">
                    <TableHead>
                        <TableRow><TableCell data-help="data.webhooks.col.when">When</TableCell><TableCell data-help="data.webhooks.col.event">Event</TableCell><TableCell data-help="data.webhooks.col.status">Status</TableCell><TableCell data-help="data.webhooks.col.attempts" align="right">Attempts</TableCell><TableCell data-help="data.webhooks.col.result">Last result</TableCell><TableCell /></TableRow>
                    </TableHead>
                    <TableBody>
                        {deliveries.length === 0 && <TableRow><TableCell colSpan={6}>No deliveries yet.</TableCell></TableRow>}
                        {deliveries.map(d => (
                            <TableRow key={d.id}>
                                <TableCell>{d.createdAt ? formatDateTime(d.createdAt) : ''}</TableCell>
                                <TableCell>{d.eventType}</TableCell>
                                <TableCell>{d.status}</TableCell>
                                <TableCell align="right">{d.attempts}</TableCell>
                                <TableCell>{d.lastError ?? (d.lastResponseCode ? `HTTP ${d.lastResponseCode}` : '')}</TableCell>
                                <TableCell align="right">
                                    {d.status === 'FAILED' && (
                                        <Button data-help="data.webhooks.action.retry" size="small" onClick={() => act(() => WebhookService.retry(d.id), 'Re-queued', 'Could not retry')}>Retry</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm" data-help-page="data.webhooks.editor.page">
                <DialogTitle>{editing === 'new' ? 'Add webhook' : 'Edit webhook'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField label="Name" size="small" data-help="data.webhooks.editor.field.name" value={name} onChange={e => setName(e.target.value)} fullWidth inputProps={{ maxLength: 100 }} sx={{ mt: 1 }} />
                    <TextField label="URL (https)" size="small" data-help="data.webhooks.editor.field.url" value={url} onChange={e => setUrl(e.target.value)} fullWidth inputProps={{ maxLength: 500 }} />
                    <FormGroup>
                        {(settings?.events ?? []).map(ev => (
                            <FormControlLabel key={ev} data-help="data.webhooks.editor.field.event" control={<Checkbox checked={events.includes(ev)} onChange={() => toggleEvent(ev)} />} label={ev} />
                        ))}
                    </FormGroup>
                    <FormControlLabel data-help="data.webhooks.editor.field.active" control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />} label="Active" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)} data-help="data.webhooks.editor.cancel">Cancel</Button>
                    <Button variant="contained" disabled={!name.trim() || !url.trim() || events.length === 0 || busy} onClick={save} data-help="data.webhooks.action.save" {...helpWhy(!busy && (!name.trim() || !url.trim() || events.length === 0) ? { kind: 'status', message: 'Fill in the name and the https address, and tick at least one event.' } : null)}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WebhooksPanel;
